interface Props {
  /** True once a participant with `kind === ParticipantKind.AGENT` is in the room. */
  botPresent: boolean;
  /** True only when an egress-backed recording is actually capturing. Drives
   *  the "Recording + …" prefix so the badge no longer conflates
   *  transcription (bot in room) with recording (separate egress). */
  isRecording?: boolean;
}

/**
 * Persistent indicator that transcription is active. Mirrored to both
 * parties (per docs/client-frontend/live-transcript-frontend.md §7 —
 * clients should see they are being transcribed). The "Recording +"
 * prefix appears only when `isRecording === true`; otherwise the badge
 * reports transcript state only. Bot-in-room ≠ recording: the bot is the
 * transcription agent, recording is a separate practitioner-controlled
 * egress.
 *
 * Position: stacked under CallStatusChip on the top-left so it doesn't
 * collide with the LocalParticipantPip floating in the top-right.
 */
export function TranscriptionActiveBadge({ botPresent, isRecording = false }: Props) {
  const dotClass = !botPresent
    ? 'h-2 w-2 animate-pulse rounded-full bg-[#D4A017]'
    : isRecording
      ? 'h-2 w-2 rounded-full bg-[#C15A4D]'
      : 'h-2 w-2 rounded-full bg-[#D4A017]';

  const label = !botPresent
    ? 'Transcript starting…'
    : isRecording
      ? 'Recording + Transcript active'
      : 'Transcript active';

  return (
    <output
      aria-live="polite"
      className="pointer-events-none absolute top-16 left-5 z-30 flex items-center gap-2 rounded-full bg-black/55 px-3 py-1.5 text-[12px] tracking-wide text-white/90 backdrop-blur-sm"
    >
      <span aria-hidden="true" className={dotClass} />
      <span>{label}</span>
    </output>
  );
}
